/**
 * V210.2: Entitlement Service
 * Centralized resolver for all premium feature access
 * 
 * Single source of truth for:
 * - Verified badge access
 * - Trust score bonus
 * - Priority ranking
 * - Premium support
 * 
 * Location: src/services/entitlement/EntitlementService.ts
 * 
 * Usage:
 * import { EntitlementService } from '@/services/entitlement/EntitlementService';
 * 
 * const entitlements = await EntitlementService.resolveEntitlements(userId);
 * if (entitlements.isVerified) {
 *   // Show verified badge
 * }
 */

import { UnifiedSubscriptionService, type SubscriptionProvider } from '@/services/subscription/UnifiedSubscriptionService';
import type { SubscriptionStatus } from '@/billing/types';
import { VERIFIED_PRODUCT_CODE } from '@/config/subscriptions';

export type Entitlements = {
  isVerified: boolean;                    // Has access now (based on canonical state)
  verifiedStatus: SubscriptionStatus | null; // Current subscription status (if any)
  provider: SubscriptionProvider | null;   // Subscription provider (Google Play / Apple)
  trustScoreBonus: number;                // 100 if isVerified else 0
};

export class EntitlementService {
  private static readonly SERVICE_TAG = '🎯 V210.2: EntitlementService';
  
  // V210.2: Simple in-memory cache with TTL (30 seconds)
  // Key: userId, Value: { entitlements, timestamp }
  private static cache = new Map<string, { entitlements: Entitlements; timestamp: number }>();
  private static readonly CACHE_TTL_MS = 5 * 60 * 1000; // P2: 5 minutes (was 30s)

  /**
   * Clear cache for a specific user (call after subscription changes)
   */
  static clearCache(userId?: string): void {
    if (userId) {
      this.cache.delete(userId);
      console.log(`${this.SERVICE_TAG} - Cache cleared for user ${userId}`);
    } else {
      this.cache.clear();
      console.log(`${this.SERVICE_TAG} - All cache cleared`);
    }
  }

  /**
   * Resolve all entitlements for a user
   * Uses canonical subscription state resolver for consistent logic
   * Includes simple memoization (30s TTL) to prevent excessive calls
   * 
   * @param userId - User ID to check entitlements for
   * @param forceRefresh - If true, bypass cache and refresh
   * @returns Entitlements object with access flags and bonuses
   */
  static async resolveEntitlements(userId: string, forceRefresh = false): Promise<Entitlements> {
    // V242: Defensive guard - if userId is missing, return default entitlements immediately
    if (!userId) {
      console.log(`${this.SERVICE_TAG} - No userId provided, returning default entitlements`);
      return {
        isVerified: false,
        verifiedStatus: null,
        provider: null,
        trustScoreBonus: 0,
      };
    }

    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = this.cache.get(userId);
      if (cached) {
        const age = Date.now() - cached.timestamp;
        if (age < this.CACHE_TTL_MS) {
          console.log(`${this.SERVICE_TAG} - Using cached entitlements for user ${userId} (age: ${age}ms)`);
          return cached.entitlements;
        }
        // Cache expired, remove it
        this.cache.delete(userId);
      }
    }
    console.log(`${this.SERVICE_TAG} - Resolving entitlements for user ${userId}`);

    try {
      // Get valid subscription using canonical state resolver
      const subscription = await UnifiedSubscriptionService.getValidSubscription(
        userId,
        VERIFIED_PRODUCT_CODE
      );

      if (!subscription) {
        console.log(`${this.SERVICE_TAG} - No valid subscription found for user ${userId}`);
        const noSubEntitlements: Entitlements = {
          isVerified: false,
          verifiedStatus: null,
          provider: null,
          trustScoreBonus: 0,
        };
        // V242: Cache "no subscription" result to prevent repeated queries
        this.cache.set(userId, {
          entitlements: noSubEntitlements,
          timestamp: Date.now(),
        });
        return noSubEntitlements;
      }

      // Use canonical state resolver to determine access
      const state = UnifiedSubscriptionService.resolveSubscriptionState(subscription);
      const isVerified = state.hasAccess;

      console.log(`${this.SERVICE_TAG} - User ${userId} entitlements:`, {
        isVerified,
        verifiedStatus: subscription.status,
        provider: subscription.provider,
        trustScoreBonus: isVerified ? 100 : 0,
      });

      const entitlements: Entitlements = {
        isVerified,
        verifiedStatus: subscription.status,
        provider: subscription.provider,
        trustScoreBonus: isVerified ? 100 : 0,
      };

      // Cache the result
      this.cache.set(userId, {
        entitlements,
        timestamp: Date.now(),
      });

      return entitlements;
    } catch (error) {
      // V242: Catch ANY error including query builder issues like "eq is not a function"
      console.error(`${this.SERVICE_TAG} - Error resolving entitlements (fail-safe):`, error);
      // Fail safe: return no entitlements on error - NEVER throw
      const fallback: Entitlements = {
        isVerified: false,
        verifiedStatus: null,
        provider: null,
        trustScoreBonus: 0,
      };
      
      // Don't cache errors
      return fallback;
    }
  }
}

export default EntitlementService;
